import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import '../../i18n';
import './app.sass';
import { BasePublic } from '../baseLayouts/public/index';
import { BasePrivate } from '../baseLayouts/private/index';
import { ConfigContext } from '../../hooks/context/config';
import { LoginContext, UserContext } from '../../hooks/context/userInfo';
import { LangContext, AllLanguages, LangUrlContext } from '../../hooks/context/lang';
import { LoadingOutlined } from '@ant-design/icons';
import { BrowserRouter as Router } from 'react-router-dom';
import { LocalizedRouter } from '../../routes/public/routes';
import { AppLanguage } from '../../routes/appLanguages';
import { appStrings } from '../../routes/localizations/index';
import { LocalizedRouterPrivate } from '../../routes/private/routes';
import { CookiesProvider } from 'react-cookie';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { RestApiClientContext } from '../../hooks/context/pageContext';
import { RestApiClient } from '../../api/restClient';
import { DURATION_COOKIE } from 'src/constants/constants';
import { Spin } from 'antd';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import ServiceUnavailable from './ServiceUnavailable503';

// Componente principal de nuestra aplicación y que renderizamos en el main.tsx
export const App = (config) => {
    // Libreria para el manejador de idioma de la app
    const { t, i18n } = useTranslation();

    const restApiClient = useContext(RestApiClientContext);
    // Contexto con el idioma de usuario y en la que se visualizara la app
    const language = useContext(LangContext);
    // Contexto con el idioma URL
    const languageUrl = useContext(LangUrlContext);
    // Contexto con todos los idiomas disponibles en el sistema para app
    const allLang = useContext(AllLanguages);
    // Contexto con la configuracion del cliente obtenida de BBDD y poder visualizar logo, color principal, etc
    const configuration = useContext(ConfigContext);
    // Contexto con los datos del usuario si esta logado
    const userContext = useContext(UserContext);
    const [login, setLogin] = useState(false);
    const [isAppReady, setIsAppReady] = useState(false);
    const [userLogin, setUserToken, removeCookie] = useCookies(['userToken']);

    let initLang: string = '';
    let initLangURL: string = config.allLanguages.filter((f) => f.id === initLang).shortId;

    if (language.GetLangContext() === '') {
        initLang = config.userLang;
    } else {
        initLang = language.GetLangContext();
    }

    // Carga inicial con el color destacado del cliente
    const style =
        configuration &&
        `
    :root {
        --primary-color: ${configuration.colour};
        --beds-to-b-color-dark: #f26a2c;
        --beds-to-b-color-green: #44b8ae;
    }
  `;

    // Actualizar la libreria i18n para el idioma de la web
    useEffect(() => {
        i18n.changeLanguage(initLang);
        allLang.UpdateAllLangContext(config.allLanguages);
        configuration.UpdateConfigContext(config.config);
        language.UpdateLangContext(initLang);
        languageUrl.UpdateUrlLangContext(initLangURL);
    }, []);

    useEffect(() => {
        async function authoritationCheck() {
            if (
                userLogin.userToken &&
                userLogin.userToken !== 'null' &&
                !userContext.GetNameUser()
            ) {
                try {
                    const response = await restApiClient.getUserName();

                    const expireDate = new Date(Date.now() + DURATION_COOKIE);
                    setUserToken('userToken', userLogin.userToken, {
                        path: '/',
                        expires: expireDate,
                    });
                    sessionStorage.setItem('token', userLogin.userToken);

                    userContext.UpdateUserContext(
                        response.name,
                        languageUrl.GetURLLangContext(),
                        language.GetLangContext(),
                        response.creationUserType,
                    );

                    setLogin(true);
                } catch (error) {
                    // Si falla la validación, limpiar y mostrar pública
                    sessionStorage.removeItem('token');
                    removeCookie('userToken', { path: '/' });
                    setLogin(false);
                } finally {
                    setIsAppReady(true);
                }
            } else {
                // Sin token o ya cargado
                setIsAppReady(true);
            }
        }
        authoritationCheck();
    }, []);

    if (!isAppReady)
        return (
            <div
                style={{
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Spin
                    tip="Loading..."
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
            </div>
        );

    return (
        // En el fallback podemos añadir un spinner (<Spinner/>)
        <>
            <LoginContext.Provider
                value={{
                    login,
                    updateValue: (key) => {
                        setLogin(key);
                    },
                }}
            >
                <CookiesProvider>
                    {login || typeof userLogin.userToken !== 'undefined' ? (
                        <RestApiClientContext.Provider
                            value={new RestApiClient(userLogin.userToken, t)}
                        >
                            <LocalizedRouterPrivate
                                RouterComponent={Router}
                                languages={AppLanguage}
                                appStrings={appStrings}
                                defaultLanguage={initLangURL}
                            >
                                <style>{style}</style>
                                <BasePrivate />
                            </LocalizedRouterPrivate>
                        </RestApiClientContext.Provider>
                    ) : (
                        <LocalizedRouter
                            RouterComponent={Router}
                            languages={AppLanguage}
                            appStrings={appStrings}
                            defaultLanguage={initLangURL}
                        >
                            <style>{style}</style>
                            <BasePublic />
                        </LocalizedRouter>
                    )}
                </CookiesProvider>
            </LoginContext.Provider>
        </>
    );
};
